import { Flex, Modal } from 'antd';
import useModal from 'antd/es/modal/useModal';
import { Button, Form, Table } from 'antd/lib';
import { map } from 'lodash';
import moment from 'moment';
import { read, utils, writeFile } from 'xlsx';
import { useBulkCreateEmployeeSalaryAdjustments } from '../../utils/api-hooks/employee.hook';

export function EmployeeSalaryAdjustmentsImportForm() {
  const [modal, contextHolder] = useModal();
  const [form] = Form.useForm<{ file: File }>();
  const { mutateAsync: bulkCreateSalaryAdjustments } =
    useBulkCreateEmployeeSalaryAdjustments();

  const onDownloadTemplateClick = () => {
    const template = [
      {
        日期: moment().format('YYYY-MM-DD'),
        姓名: '王小明',
        名目: '全勤獎金',
        金額: 1000,
        備註: '我是範例請刪除我（罰款請在金額填負數）',
      },
    ];

    const wb = utils.book_new();
    const ws = utils.json_to_sheet(template);
    utils.book_append_sheet(wb, ws, '薪資調整範本');
    writeFile(wb, '薪資調整範本.xlsx');
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onload = function (event) {
      const data = event.target!.result;

      // 讀成 workbook（可讀取多個 sheet）
      const workbook = read(data, { type: 'array', cellDates: true });

      // 假設只讀第一個 sheet
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];

      // 將 sheet 轉為 JSON
      const json = utils.sheet_to_json<{
        日期: string;
        姓名: string;
        名目: string;
        金額: number;
        備註: string;
      }>(worksheet);

      const items = map(json, (item) => {
        return {
          time: moment(item['日期']).utcOffset(8).startOf('day'),
          employeeName: item['姓名'],
          type: item['名目'],
          amount: parseInt(String(item['金額']), 10),
          note: item['備註'],
        };
      });

      modal.confirm({
        title: '匯入薪資調整項目',
        width: '65%',
        content: (
          <Flex vertical gap={8} align="stretch">
            <p>以下是匯入的資料，請確認無誤後提交。</p>
            <Table
              scroll={{ x: 'max-content' }}
              size="small"
              dataSource={items}
              pagination={false}
              columns={[
                {
                  title: '日期',
                  dataIndex: 'time',
                  key: 'date',
                  render: (value) => moment(value).format('YYYY-MM-DD'),
                },
                { title: '姓名', dataIndex: 'employeeName', key: 'name' },
                { title: '名目', dataIndex: 'type', key: 'item' },
                { title: '金額', dataIndex: 'amount', key: 'amount' },
                { title: '備註', dataIndex: 'note', key: 'remarks' },
              ]}
              rowKey={(record, index) =>
                `${record.employeeName}-${record.type}-${index}`
              }
            />
          </Flex>
        ),
        icon: null,
        okText: '確認匯入',
        cancelText: '取消',
        onOk: async () => {
          try {
            // 準備批量建立的資料，轉換成後端 API 需要的格式
            const bulkCreateData = {
              data: items.map((item) => ({
                time: item.time.toISOString(),
                employeeName: item.employeeName,
                type: item.type,
                amount: item.amount,
                note: item.note,
              })),
            };

            await bulkCreateSalaryAdjustments(bulkCreateData);

            Modal.destroyAll();
          } catch (error) {
            console.error('匯入失敗:', error);
            // 錯誤處理已經在 hook 中處理，這裡不需要額外處理
          }
          // 清除檔案選擇並關閉所有模態框
          e.target.value = '';
        },
        onCancel() {
          e.target.value = ''; // 清除選擇的檔案
        },
      });
    };

    reader.readAsArrayBuffer(file);
  };

  return (
    <Flex vertical gap={8} align="start">
      {contextHolder}
      <Button onClick={onDownloadTemplateClick} type="link">
        下載範本
      </Button>
      <Form.Item label="請上傳薪資調整項目" rules={[{ required: true }]}>
        <input type="file" accept=".xlsx" onChange={onFileChange} />
      </Form.Item>
      <Form form={form} autoComplete="off" preserve={false}></Form>
    </Flex>
  );
}
